/**
 * /catalog — public, no-auth "All Dogs" registry browse.
 *
 * Privacy-safe top-of-funnel page: hero stats + breed-chip filter +
 * dog photo grid. No owner identity, microchip, DOB, or location is
 * ever exposed. Dogs `for_sale=true` get a gold "For sale" ribbon and
 * deep-link into /marketplace; everyone else clicks through to the
 * existing public dog page at /public/dogs/{id}.
 *
 * Grouped by breed via a sticky chip strip — tapping a chip filters
 * the grid in place. "All breeds" returns to the unfiltered view.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import { DogIcon as Dog, StorefrontIcon as Storefront, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { photoUrl, thumbUrl } from "@/lib/utils-app";

const PAGE_SIZE = 60;

export default function PublicCatalog() {
  const [stats, setStats] = useState(null);
  const [breeds, setBreeds] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const breedFilter = params.get("breed") || "";
  const forSaleOnly = params.get("for_sale") === "1";
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/catalog/stats"),
      api.get("/catalog/breeds"),
    ]).then(([s, b]) => {
      if (cancelled) return;
      setStats(s.data);
      setBreeds(b.data?.breeds || []);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
    if (breedFilter) q.set("breed", breedFilter);
    if (forSaleOnly) q.set("for_sale", "true");
    api.get(`/catalog/dogs?${q.toString()}`)
      .then((r) => {
        if (skip === 0) {
          setDogs(r.data?.dogs || []);
        } else {
          setDogs((prev) => [...prev, ...(r.data?.dogs || [])]);
        }
        setTotal(r.data?.total || 0);
      })
      .finally(() => setLoading(false));
  }, [breedFilter, forSaleOnly, skip]);

  // Reset pagination when filters change.
  useEffect(() => { setSkip(0); }, [breedFilter, forSaleOnly]);

  const setBreed = (b) => {
    const next = new URLSearchParams(params);
    if (b) next.set("breed", b); else next.delete("breed");
    setParams(next);
  };

  const toggleForSale = () => {
    const next = new URLSearchParams(params);
    if (forSaleOnly) next.delete("for_sale"); else next.set("for_sale", "1");
    setParams(next);
  };

  // Group dogs into breed buckets when no filter is active so the
  // visitor sees the registry's variety at a glance.
  const grouped = useMemo(() => {
    if (breedFilter) return null;
    const byBreed = {};
    dogs.forEach((d) => {
      const b = d.breed || "Mixed / Other";
      (byBreed[b] = byBreed[b] || []).push(d);
    });
    return Object.entries(byBreed)
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [dogs, breedFilter]);

  const hasMore = skip + PAGE_SIZE < total;

  return (
    <PublicShell>
      <div className="px-4 sm:px-8 max-w-7xl mx-auto pb-20">
        {/* Hero */}
        <section className="pt-10 sm:pt-16 pb-8 border-b border-[#E5E5E5]">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-gold" /> The registry
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            Every dog in the registry, public-safe.
          </h1>
          <p className="text-sm sm:text-base text-[#3F3F3F] mt-4 max-w-2xl leading-relaxed">
            Browse the breeds and dogs registered with Armstrong K9 Registry.
            Personal information stays private — only the dog's name, breed,
            and photo are shown. Tap any dog with a "For sale" ribbon to see
            their marketplace listing.
          </p>
          {stats && (
            <div className="grid grid-cols-3 gap-3 mt-8 max-w-2xl" data-testid="catalog-stats">
              <Tile label="Registered dogs" value={stats.total_dogs} />
              <Tile label="Breeds represented" value={stats.breeds_represented} />
              <Tile label="Available now" value={stats.for_sale_now} highlight />
            </div>
          )}
        </section>

        {/* Filter strip — sticky on scroll so the breed chips stay in reach */}
        <div className="sticky top-[64px] z-20 bg-white/90 backdrop-blur-md -mx-4 sm:-mx-8 px-4 sm:px-8 py-3 border-b border-[#E5E5E5] my-4">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1">
            <button
              type="button"
              onClick={() => setBreed("")}
              data-testid="catalog-chip-all"
              className={chipCls(!breedFilter)}
            >
              All breeds <span className="ml-1 opacity-60 tabular-nums">{stats?.total_dogs ?? "—"}</span>
            </button>
            {breeds.map((b) => (
              <button
                key={b.name}
                type="button"
                onClick={() => setBreed(b.name)}
                data-testid={`catalog-chip-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={chipCls(breedFilter.toLowerCase() === b.name.toLowerCase())}
              >
                {b.name} <span className="ml-1 opacity-60 tabular-nums">{b.count}</span>
              </button>
            ))}
            <span className="border-l border-[#E5E5E5] h-6 mx-1 flex-shrink-0" />
            <button
              type="button"
              onClick={toggleForSale}
              data-testid="catalog-chip-for-sale"
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                forSaleOnly
                  ? "bg-gold text-ink border-gold"
                  : "bg-white text-[#3F3F3F] border-[#E5E5E5] hover:border-ink"
              }`}
            >
              <Storefront size={12} weight="fill" /> For sale only
            </button>
          </div>
        </div>

        {/* Grid */}
        {loading && skip === 0 ? (
          <p className="text-center text-[#6B6B6B] py-16">Loading…</p>
        ) : dogs.length === 0 ? (
          <EmptyState breed={breedFilter} forSaleOnly={forSaleOnly} />
        ) : grouped ? (
          // Default view — grouped by breed
          <div className="space-y-12">
            {grouped.map(([b, list]) => (
              <BreedSection key={b} breed={b} dogs={list} setBreed={setBreed} />
            ))}
          </div>
        ) : (
          // Filtered view — flat grid
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {dogs.map((d) => <DogCard key={d.id} dog={d} />)}
          </div>
        )}

        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              type="button"
              onClick={() => setSkip((s) => s + PAGE_SIZE)}
              disabled={loading}
              data-testid="catalog-load-more"
              className="rounded-full bg-ink text-white px-6 py-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] hover:bg-crimson transition-colors disabled:opacity-50"
            >
              {loading ? "Loading…" : `Show more (${total - dogs.length} more)`}
            </button>
          </div>
        )}
      </div>
    </PublicShell>
  );
}

function chipCls(active) {
  return `flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border whitespace-nowrap transition-colors ${
    active
      ? "bg-ink text-white border-ink"
      : "bg-white text-[#3F3F3F] border-[#E5E5E5] hover:border-ink"
  }`;
}

function Tile({ label, value, highlight = false }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-gold/40 bg-[#FBF7EC]" : "border-[#E5E5E5] bg-white"}`}>
      <div className="font-spectral text-3xl tabular-nums leading-none text-ink">{value ?? "—"}</div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mt-2">{label}</div>
    </div>
  );
}

function BreedSection({ breed, dogs, setBreed }) {
  return (
    <section data-testid={`breed-section-${breed.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <h2 className="font-spectral text-2xl sm:text-3xl tracking-tight">
          {breed} <span className="text-[#6B6B6B] text-[14px] tabular-nums font-normal">· {dogs.length}</span>
        </h2>
        <button
          type="button"
          onClick={() => setBreed(breed)}
          className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink hover:text-crimson transition-colors"
        >
          See all {breed} →
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {dogs.slice(0, 10).map((d) => <DogCard key={d.id} dog={d} />)}
      </div>
    </section>
  );
}

function DogCard({ dog }) {
  return (
    <Link
      to={`/public/dogs/${dog.id}`}
      data-testid={`catalog-dog-${dog.id}`}
      className="group block rounded-xl overflow-hidden border border-[#E5E5E5] bg-white hover:border-ink/40 hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-[#FAFAFA] relative">
        {dog.photo ? (
          <img
            src={thumbUrl(dog.photo) || photoUrl(dog.photo)}
            alt={`${dog.name} — ${dog.breed}`}
            loading="lazy"
            onError={(e) => { e.currentTarget.src = photoUrl(dog.photo); }}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog size={32} weight="duotone" className="text-[#9CA3AF]" />
          </div>
        )}
        {dog.for_sale && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-gold text-ink text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 shadow-sm">
            <Storefront size={10} weight="fill" /> For sale
          </span>
        )}
      </div>
      <div className="p-2.5">
        <p className="font-spectral text-[15px] leading-tight tracking-tight text-ink truncate">{dog.name}</p>
        <p className="text-[11px] text-[#6B6B6B] uppercase tracking-[0.12em] truncate">
          {dog.breed}{dog.sex ? ` · ${dog.sex === "male" ? "M" : "F"}` : ""}
        </p>
      </div>
    </Link>
  );
}

function EmptyState({ breed, forSaleOnly }) {
  return (
    <div className="text-center py-20" data-testid="catalog-empty">
      <Dog size={48} weight="duotone" className="text-[#9CA3AF] mx-auto mb-3" />
      <p className="font-spectral text-xl tracking-tight">
        {forSaleOnly && breed
          ? `No ${breed} are listed for sale right now`
          : forSaleOnly
            ? "No dogs are listed for sale right now"
            : breed
              ? `No ${breed} dogs in the registry yet`
              : "No dogs registered yet"}
      </p>
      <p className="text-[12px] text-[#6B6B6B] mt-2">Check back soon — new dogs join every week.</p>
    </div>
  );
}
